import { useCallback, useState } from 'react';
import { getClientDomain, getToken } from '../../../utils/Common';
import {
  MOCK_ROLES,
  MOCK_MODULES,
  MOCK_STANDARD_ACTIONS,
  MOCK_CUSTOM_BY_MODULE,
  buildInitialMockPerms,
} from './permissionsMockData';

const STANDARD_CODES = new Set(['view', 'search', 'create', 'update', 'delete', 'export']);

function isApiAccessDenied(status, json) {
  if (status === 401 || status === 403) return true;
  if (!json || typeof json !== 'object') return false;
  const c = Number(json.code);
  if (c === 401 || c === 403) return true;
  const m = String(json.message || '');
  return (
    m === 'UNAUTHORIZED_USER' ||
    m === 'BLI_ACTION_FORBIDDEN' ||
    m === 'BLI_PERMISSIONS_UNAVAILABLE'
  );
}

/** RS3 from UspGetBliPermissionsSettings: una fila por (ModuleId, ActionId) en ServModuleActionsBli. */
function buildActionsByModuleFromApi(flatActions) {
  const toLabel = (code) => {
    const s = String(code || '').trim();
    if (!s) return '';
    return s
      .split('_')
      .filter(Boolean)
      .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  };
  const groupOrder = (g) => {
    const key = String(g || '').trim().toLowerCase();
    const map = { read: 10, search: 20, create: 30, update: 40, delete: 50, export: 60, meta: 90 };
    return map[key] ?? 80;
  };
  const by = {};
  (flatActions || []).forEach((row) => {
    const code = row.actionCode ?? row.ActionCode;
    if (!code || typeof code !== 'string') return;
    const mid = String(row.moduleId ?? row.ModuleId ?? '');
    if (!mid) return;
    const actionGroup = row.actionGroup ?? row.ActionGroup ?? null;
    if (!by[mid]) by[mid] = [];
    by[mid].push({
      actionId: row.actionId ?? row.ActionId ?? null,
      code: String(code),
      labelKey: null,
      label: toLabel(code) || String(code),
      desc: '',
      descKey: null,
      deps: [],
      actionGroup,
    });
  });
  Object.keys(by).forEach((k) => {
    by[k].sort((a, b) => {
      const go = groupOrder(a.actionGroup) - groupOrder(b.actionGroup);
      if (go !== 0) return go;
      const la = String(a.label || a.code || '');
      const lb = String(b.label || b.code || '');
      const lc = la.localeCompare(lb);
      if (lc !== 0) return lc;
      return (Number(a.actionId) || 0) - (Number(b.actionId) || 0);
    });
  });
  return by;
}

function mapApiModule(row) {
  return {
    id: String(row.moduleId ?? row.ModuleId ?? row.id),
    parentModuleId:
      row.parentModuleId != null && row.parentModuleId !== ''
        ? String(row.parentModuleId)
        : row.ParentModuleId != null && row.ParentModuleId !== ''
          ? String(row.ParentModuleId)
          : null,
    moduleName: row.moduleName ?? row.ModuleName ?? row.DescDetalle ?? '',
    path: row.path ?? row.Path ?? '',
  };
}

function groupCustomActions(flatActions) {
  const by = {};
  (flatActions || []).forEach((row) => {
    const code = row.actionCode ?? row.ActionCode ?? row.DescDetalle;
    if (!code || STANDARD_CODES.has(code)) return;
    const mid = String(row.moduleId ?? row.ModuleId);
    if (!by[mid]) by[mid] = [];
    by[mid].push({
      actionId: row.actionId ?? row.ActionId,
      code,
      label: row.label ?? row.Label ?? code,
      desc: row.description ?? row.Description ?? '',
      deps: [],
      actionGroup: row.actionGroup ?? row.ActionGroup,
    });
  });
  return by;
}

/**
 * Build normalized catalog from API or fall back to mock.
 */
export function usePermissionsCatalog() {
  const [source, setSource] = useState('mock'); // 'api' | 'mock' | 'error'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** API respondió 403/401 (u otro cierre explícito): no usar datos demo. */
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modules, setModules] = useState([]);
  const [customByModule, setCustomByModule] = useState({});
  /** Catálogo desde GET …/bli-permissions/settings (RS3 = ServModuleActionsBli); sin acciones “estándar” mock. */
  const [granularCatalog, setGranularCatalog] = useState(false);
  const [roles, setRoles] = useState([]);
  const [initialPerms, setInitialPerms] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    const token = getToken?.() ?? sessionStorage.getItem('token');
    const base = getClientDomain(2);

    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const settingsRes = await fetch(`${base}/users/bli-permissions/settings`, { method: 'GET', headers });
      const settingsJson = await settingsRes.json().catch(() => null);

      if (isApiAccessDenied(settingsRes.status, settingsJson)) {
        setPermissionDenied(true);
        setModules([]);
        setRoles([]);
        setCustomByModule({});
        setGranularCatalog(false);
        setInitialPerms(null);
        setSource('api');
        return;
      }

      const unifiedOk =
        settingsJson?.code === 200 &&
        Array.isArray(settingsJson.payload?.modules) &&
        settingsJson.payload.modules.length > 0;

      let catJson = null;
      let rolesJson = null;

      let fromUnifiedSettings = false;
      if (unifiedOk) {
        fromUnifiedSettings = true;
        catJson = {
          code: 200,
          payload: {
            modules: settingsJson.payload.modules,
            actions: settingsJson.payload.actions,
          },
        };
        rolesJson = {
          code: 200,
          payload: settingsJson.payload.roles,
        };
      } else {
        const [catRes, rolesRes] = await Promise.all([
          fetch(`${base}/users/bli-permissions/catalog`, { method: 'GET', headers }),
          fetch(`${base}/dashboard/clients/rolesBli`, { method: 'GET', headers }),
        ]);
        const catParsed = await catRes.json().catch(() => null);
        const rolesParsed = await rolesRes.json().catch(() => null);
        if (isApiAccessDenied(catRes.status, catParsed) || isApiAccessDenied(rolesRes.status, rolesParsed)) {
          setPermissionDenied(true);
          setModules([]);
          setRoles([]);
          setCustomByModule({});
          setGranularCatalog(false);
          setInitialPerms(null);
          setSource('api');
          return;
        }
        catJson = catRes.ok ? catParsed : null;
        rolesJson = rolesRes.ok ? rolesParsed : null;
      }

      const apiModules = catJson?.payload?.modules;
      const apiActions = catJson?.payload?.actions;

      if (catJson?.code === 200 && Array.isArray(apiModules) && apiModules.length > 0) {
        const mods = apiModules.map(mapApiModule);
        if (fromUnifiedSettings) {
          setCustomByModule(buildActionsByModuleFromApi(apiActions));
          setGranularCatalog(true);
        } else {
          setGranularCatalog(false);
          const custom = apiActions?.length ? groupCustomActions(apiActions) : {};
          setCustomByModule(custom);
        }
        setModules(mods);
        setSource('api');
        setInitialPerms({}); // loaded per role on demand
      } else {
        // No usar datos mock como fallback (evita editar "datos demo" creyendo que son reales).
        setModules([]);
        setCustomByModule({});
        setGranularCatalog(false);
        setInitialPerms(null);
        setSource('error');
        setError('CATALOG_UNAVAILABLE');
      }

      const rolesList = rolesJson?.payload;
      if (rolesJson?.code === 200 && Array.isArray(rolesList) && rolesList.length > 0) {
        setRoles(
          rolesList.map((r) => ({
            id: r.Id ?? r.id,
            name: r.Name ?? r.name ?? String(r.Id ?? r.id),
            abbr: ((r.Name ?? r.name) || '?').slice(0, 2).toUpperCase(),
            color: '#6b7280',
            system: false,
            userCount: 0,
          }))
        );
      } else {
        // Sin roles reales, no usar mock.
        setRoles([]);
      }
    } catch (e) {
      setError(e.message || 'LOAD_ERROR');
      setPermissionDenied(false);
      setModules([]);
      setCustomByModule({});
      setGranularCatalog(false);
      setRoles([]);
      setInitialPerms(null);
      setSource('error');
    } finally {
      setLoading(false);
    }
  }, []);

  const createModuleLocal = useCallback((draft) => {
    const name = String(draft?.moduleName || '').trim();
    const path = String(draft?.path || '').trim();
    const parentModuleIdRaw = draft?.parentModuleId;
    const parentModuleId =
      parentModuleIdRaw === null || parentModuleIdRaw === undefined || String(parentModuleIdRaw).trim() === ''
        ? null
        : String(parentModuleIdRaw);

    if (!name || !path) {
      return { ok: false, error: 'REQUIRED_FIELDS' };
    }

    const id = `tmp-${Date.now()}`;
    const next = {
      id,
      parentModuleId,
      moduleName: name,
      path,
    };

    setModules((prev) => {
      const arr = Array.isArray(prev) ? prev.slice() : [];
      arr.push(next);
      return arr;
    });

    return { ok: true, module: next };
  }, []);

  const createModuleApi = useCallback(
    async (draft) => {
      const name = String(draft?.moduleName || '').trim();
      const path = String(draft?.path || '').trim();
      const parentRaw = draft?.parentModuleId;
      const parentModuleId =
        parentRaw === null || parentRaw === undefined || String(parentRaw).trim() === ''
          ? null
          : Number(parentRaw);
      if (!name || !path) {
        return { ok: false, error: 'REQUIRED_FIELDS' };
      }
      const token = getToken?.() ?? sessionStorage.getItem('token');
      const base = getClientDomain(2);
      try {
        const res = await fetch(`${base}/users/bli-permissions/modules`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            moduleName: name,
            path,
            parentModuleId: parentModuleId != null && !Number.isNaN(parentModuleId) ? parentModuleId : null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200) {
          return { ok: false, error: data.message || 'CREATE_FAILED', detail: data.detail };
        }
        await load();
        return { ok: true, module: data.payload };
      } catch (e) {
        return { ok: false, error: e.message || 'CREATE_FAILED' };
      }
    },
    [load]
  );

  const updateModuleLocal = useCallback((moduleId, draft) => {
    const name = String(draft?.moduleName || '').trim();
    const path = String(draft?.path || '').trim();
    const parentModuleIdRaw = draft?.parentModuleId;
    const parentModuleId =
      parentModuleIdRaw === null || parentModuleIdRaw === undefined || String(parentModuleIdRaw).trim() === ''
        ? null
        : String(parentModuleIdRaw);

    if (!name || !path) {
      return { ok: false, error: 'REQUIRED_FIELDS' };
    }

    setModules((prev) =>
      (Array.isArray(prev) ? prev : []).map((m) =>
        String(m.id) === String(moduleId)
          ? { ...m, moduleName: name, path, parentModuleId }
          : m
      )
    );

    return { ok: true };
  }, []);

  const createActionLocal = useCallback((moduleId, draft) => {
    const code = String(draft?.actionCode || '').trim();
    const mid = String(moduleId);
    if (!code) {
      return { ok: false, error: 'ACTION_CODE_REQUIRED' };
    }
    const groupOrder = (g) => {
      const key = String(g || '').trim().toLowerCase();
      const map = { read: 10, search: 20, create: 30, update: 40, delete: 50, export: 60, meta: 90 };
      return map[key] ?? 80;
    };
    const toLabel = (c) => {
      const s = String(c || '').trim();
      if (!s) return '';
      return s
        .split('_')
        .filter(Boolean)
        .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');
    };
    const actionLabel = toLabel(code) || code;
    setCustomByModule((prev) => {
      const list = [...(prev[mid] || [])];
      list.push({
        actionId: null,
        code,
        labelKey: null,
        label: actionLabel,
        desc: '',
        descKey: null,
        deps: [],
        actionGroup: null,
      });
      list.sort((a, b) => {
        const go = groupOrder(a.actionGroup) - groupOrder(b.actionGroup);
        if (go !== 0) return go;
        return String(a.label || a.code || '').localeCompare(String(b.label || b.code || ''));
      });
      return { ...prev, [mid]: list };
    });
    return { ok: true };
  }, []);

  const createActionApi = useCallback(
    async (moduleId, draft) => {
      const code = String(draft?.actionCode || '').trim();
      const mid = parseInt(moduleId, 10);
      if (!code) {
        return { ok: false, error: 'ACTION_CODE_REQUIRED' };
      }
      if (Number.isNaN(mid) || mid < 1) {
        return { ok: false, error: 'INVALID_MODULE_ID' };
      }
      const token = getToken?.() ?? sessionStorage.getItem('token');
      const base = getClientDomain(2);
      try {
        const res = await fetch(`${base}/users/bli-permissions/modules/${mid}/actions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            actionCode: code,
            actionGroup: null,
            active: draft?.active !== false,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200) {
          return { ok: false, error: data.message || 'CREATE_FAILED', detail: data.detail };
        }
        await load();
        return { ok: true, payload: data.payload };
      } catch (e) {
        return { ok: false, error: e.message || 'CREATE_FAILED' };
      }
    },
    [load]
  );

  const updateActionCodeLocal = useCallback((moduleId, actionId, newCode) => {
    const code = String(newCode || '').trim();
    const mid = String(moduleId);
    const aid = actionId != null ? String(actionId) : '';
    if (!code) {
      return { ok: false, error: 'ACTION_CODE_REQUIRED' };
    }
    if (!aid) {
      return { ok: false, error: 'ACTION_ID_REQUIRED' };
    }
    let missing = false;
    setCustomByModule((prev) => {
      const list = [...(prev[mid] || [])];
      const idx = list.findIndex((a) => String(a.actionId) === aid);
      if (idx < 0) {
        missing = true;
        return prev;
      }
      const next = [...list];
      next[idx] = { ...next[idx], code };
      return { ...prev, [mid]: next };
    });
    if (missing) return { ok: false, error: 'ACTION_NOT_FOUND' };
    return { ok: true };
  }, []);

  const updateActionCodeApi = useCallback(
    async (moduleId, actionId, newCode) => {
      const code = String(newCode || '').trim();
      const mid = parseInt(moduleId, 10);
      const aid = parseInt(actionId, 10);
      if (!code) {
        return { ok: false, error: 'ACTION_CODE_REQUIRED' };
      }
      if (Number.isNaN(mid) || mid < 1) {
        return { ok: false, error: 'INVALID_MODULE_ID' };
      }
      if (Number.isNaN(aid) || aid < 1) {
        return { ok: false, error: 'INVALID_ACTION_ID' };
      }
      const token = getToken?.() ?? sessionStorage.getItem('token');
      const base = getClientDomain(2);
      try {
        const res = await fetch(`${base}/users/bli-permissions/modules/${mid}/actions/${aid}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ actionCode: code }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200) {
          return { ok: false, error: data.message || 'UPDATE_FAILED', detail: data.detail };
        }
        await load();
        return { ok: true, payload: data.payload };
      } catch (e) {
        return { ok: false, error: e.message || 'UPDATE_FAILED' };
      }
    },
    [load]
  );

  const updateModuleApi = useCallback(
    async (moduleId, draft) => {
      const name = String(draft?.moduleName || '').trim();
      const path = String(draft?.path || '').trim();
      const parentRaw = draft?.parentModuleId;
      const parentModuleId =
        parentRaw === null || parentRaw === undefined || String(parentRaw).trim() === ''
          ? null
          : Number(parentRaw);
      if (!name || !path) {
        return { ok: false, error: 'REQUIRED_FIELDS' };
      }
      const mid = parseInt(moduleId, 10);
      if (Number.isNaN(mid)) {
        return { ok: false, error: 'INVALID_MODULE_ID' };
      }
      const token = getToken?.() ?? sessionStorage.getItem('token');
      const base = getClientDomain(2);
      try {
        const res = await fetch(`${base}/users/bli-permissions/modules/${mid}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            moduleName: name,
            path,
            parentModuleId:
              parentModuleId != null && !Number.isNaN(parentModuleId) ? parentModuleId : null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.code !== 200) {
          return { ok: false, error: data.message || 'UPDATE_FAILED', detail: data.detail };
        }
        await load();
        return { ok: true };
      } catch (e) {
        return { ok: false, error: e.message || 'UPDATE_FAILED' };
      }
    },
    [load]
  );

  return {
    load,
    /** Same as `load`; refresh catalog after admin mutations (e.g. create module). */
    refreshCatalog: load,
    loading,
    error,
    permissionDenied,
    source,
    modules,
    createModuleLocal,
    createModuleApi,
    createActionLocal,
    createActionApi,
    updateActionCodeLocal,
    updateActionCodeApi,
    updateModuleLocal,
    updateModuleApi,
    customByModule,
    granularCatalog,
    roles,
    initialPerms,
    mockStandardActions: MOCK_STANDARD_ACTIONS,
  };
}
